import reducer from '@redux/index';
import { atomWithStore } from 'jotai-redux';
import { atom } from 'jotai/index';
import { createStore } from 'redux';

const defaultStore = createStore(reducer);
const initialStateAtom = atomWithStore(defaultStore);

type StoreType = typeof defaultStore;
type StoreAtomType = typeof initialStateAtom;

const atomCache = new WeakMap<StoreType, StoreAtomType>();

// This atom holds the Redux store instance for the current SSR request.
// This should only be imported and used in the React route handler
// and in the atom hydration.
export const reduxStoreAtom = atom<StoreType | null>(null);

// This private atom is created only once and subscribes to the Redux store
// when it becomes available.
const storeStateAtom = atom((get) => {
	const store = get(reduxStoreAtom);

	if (!store) {
		// While the store is not set, we can't subscribe.
		// We return an atom that holds the initial state.
		return initialStateAtom;
	}

	// Once the store is available, we create and return a stable atom
	// that is subscribed to it.
	// We are using a cache to ensure we create only one atom per store instance.
	let cachedAtom = atomCache.get(store);

	if (!cachedAtom) {
		cachedAtom = atomWithStore(store);
		atomCache.set(store, cachedAtom);
	}

	return cachedAtom;
});

// This is the public atom that consumers will use. It reads the value
// from the underlying store-connected atom.
export const reduxAtom = atom((get) => get(get(storeStateAtom)));
